import React from 'react';
import { renderProcessedText } from '../../utils/textRenderer';

interface BillSummaryProps {
  content: Array<{
    header: string;
    dueAmount: string;
    currency: string;
    month: string;
    regularCost: string;
    subscriptionExtras: string;
    proRataCost: string;
    packageDetail: string;
  }>;
}

const BillSummary: React.FC<BillSummaryProps> = ({ content }) => {
  const renderCircularProgress = (month: string, regularCost: string, subscriptionExtras: string, proRataCost: string) => {
    // Determine which segments should be shown based on non-zero values
    const segments = [];
    
    if (parseFloat(regularCost) > 0) {
      segments.push({ color: '#a855f7', label: 'regular' }); // Brighter Purple
    }
    if (parseFloat(subscriptionExtras) > 0) {
      segments.push({ color: '#f97316', label: 'extras' }); // Brighter Orange
    }
    if (parseFloat(proRataCost) > 0) {
      segments.push({ color: '#22c55e', label: 'prorata' }); // Brighter Green
    }
    
    const totalSegments = segments.length;
    const radius = 25;
    const circumference = 2 * Math.PI * radius;
    const segmentLength = circumference / totalSegments;
    const gapLength = 3; // Small gap between segments
    
    return (
      <div className="bill-summary__progress">
        <svg width="60" height="60" className="bill-summary__progress-svg">
          {/* Background circle */}
          <circle
            cx="30"
            cy="30"
            r={radius}
            fill="none"
            stroke="#f3f4f6"
            strokeWidth="4"
          />
          
          {/* Render stroke segments based on non-zero values */}
          {segments.map((segment, index) => {
            const segmentStart = (index * (segmentLength + gapLength));
            const actualSegmentLength = segmentLength - gapLength;
            
            return (
              <circle
                key={index}
                cx="30"
                cy="30"
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${actualSegmentLength} ${circumference}`}
                strokeDashoffset={-segmentStart}
                transform="rotate(-90 30 30)"
              />
            );
          })}
        </svg>
        <div className="bill-summary__progress-text">{month.substring(0,3)}</div>
      </div>
    );
  };

  const renderPackageIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L2 7V17L12 22L22 17V7L12 2Z" stroke="#6b7280" strokeWidth="2" strokeLinejoin="round"/>
      <path d="M12 22V12" stroke="#6b7280" strokeWidth="2"/>
      <path d="M2 7L12 12L22 7" stroke="#6b7280" strokeWidth="2" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="bill-summary">
      {content.map((bill, index) => (
        <div key={index} className="bill-summary__card">
          <div className="bill-summary__header">{renderProcessedText(bill.header)}</div>
          
          <div className="bill-summary__main">
            <div className="bill-summary__amount-section">
              <div className="bill-summary__due-amount">
                {bill.currency}{bill.dueAmount}
              </div>
              <div className="bill-summary__amount-label">Amount Due</div>
            </div>
            
            {renderCircularProgress(bill.month, bill.regularCost, bill.subscriptionExtras, bill.proRataCost)}
          </div>
          
          <div className="bill-summary__breakdown">
            {parseFloat(bill.regularCost) > 0 && (
              <div className="bill-summary__item bill-summary__item--purple">
                <div className="bill-summary__item-indicator"></div>
                <div className="bill-summary__item-content">
                  <span className="bill-summary__item-label">Regular monthly cost</span>
                  <span className="bill-summary__item-amount">{bill.currency}{bill.regularCost}/m</span>
                </div>
              </div>
            )}
            
            {parseFloat(bill.subscriptionExtras) > 0 && (
              <div className="bill-summary__item bill-summary__item--orange">
                <div className="bill-summary__item-indicator"></div>
                <div className="bill-summary__item-content">
                  <span className="bill-summary__item-label">Subscription extras</span>
                  <span className="bill-summary__item-amount">{bill.currency}{bill.subscriptionExtras}/m</span>
                </div>
              </div>
            )}
            
            {parseFloat(bill.proRataCost) > 0 && (
              <div className="bill-summary__item bill-summary__item--green">
                <div className="bill-summary__item-indicator"></div>
                <div className="bill-summary__item-content">
                  <span className="bill-summary__item-label">One-time pro-rata charge</span>
                  <span className="bill-summary__item-amount">{bill.currency}{bill.proRataCost}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="bill-summary__package">
            <div className="bill-summary__package-icon">
              {renderPackageIcon()}
            </div>
            <div className="bill-summary__package-content">
              <div className="bill-summary__package-label">Your New Package</div>
              <div className="bill-summary__package-detail">{renderProcessedText(bill.packageDetail)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BillSummary;
